import React, { useState, useMemo, useEffect } from 'react';
import fetchApi from '../utils/api.js';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';
import * as XLSX from 'xlsx';

function ReceivableList({ onNavigateToProfile }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [selectedIdx, setSelectedIdx] = useState(0);

    const [cityFilter, setCityFilter] = useState('');
    const [appliedCityFilter, setAppliedCityFilter] = useState('');

    const fetchReceivables = async () => {
        try {
            setLoading(true);
            const data = await fetchApi('/outstanding/receivable');
            if (Array.isArray(data)) {
                setRows(data);
            }
        } catch (error) {
            console.error("Fetch Receivable Error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReceivables();
    }, []);

    const cityOptions = useMemo(() => {
        const cities = [...new Set(rows.map(r => r.city).filter(c => c && c !== '---'))].sort();
        return cities;
    }, [rows]);

    const handleSubmit = () => {
        setAppliedCityFilter(cityFilter);
        setPage(1);
    };

    const handleReset = () => {
        setCityFilter('');
        setAppliedCityFilter('');
        setSearch('');
        setPage(1);
    };

    const filteredRows = useMemo(() => {
        let result = rows;
        if (appliedCityFilter) {
            result = result.filter(r => (r.city || '') === appliedCityFilter);
        }
        if (search) {
            const lowSearch = search.toLowerCase();
            result = result.filter(r =>
                (r.tradeName || '').toLowerCase().includes(lowSearch) ||
                (r.name || '').toLowerCase().includes(lowSearch) ||
                (r.city || '').toLowerCase().includes(lowSearch) ||
                (r.mobile || '').toLowerCase().includes(lowSearch)
            );
        }
        return result;
    }, [rows, search, appliedCityFilter]);

    const currentRows = useMemo(() => {
        return filteredRows.slice((page - 1) * pageSize, page * pageSize);
    }, [filteredRows, page, pageSize]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (currentRows.length === 0) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIdx(prev => (prev + 1) % currentRows.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIdx(prev => (prev - 1 + currentRows.length) % currentRows.length);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const selectedItem = currentRows[selectedIdx];
                if (selectedItem) {
                    onNavigateToProfile && onNavigateToProfile(selectedItem);
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentRows, selectedIdx, onNavigateToProfile]);

    const handleExportExcel = () => {
        const excelData = filteredRows.map((r, idx) => ({
            'SR. NO.': idx + 1,
            'TRADE NAME': r.tradeName,
            'NAME': r.name,
            'ACCOUNT TYPE': r.accountType,
            'CITY': r.city,
            'MOBILE': r.mobile,
            'BALANCE': r.balance
        }));
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Receivables");
        XLSX.writeFile(workbook, "Receivable_Outstanding_Report.xlsx");
    };

    const handlePrint = () => {
        const printWindow = window.open('', '', 'height=600,width=800');
        printWindow.document.write('<html><head><title>Receivable Outstanding</title>');
        printWindow.document.write('<style>');
        printWindow.document.write('body { font-family: Arial, sans-serif; margin: 20px; }');
        printWindow.document.write('h1 { text-align: center; color: #0b3a54; }');
        printWindow.document.write('table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 11px; }');
        printWindow.document.write('th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }');
        printWindow.document.write('th { background-color: #dff5ea; color: #0b3a54; font-weight: 600; }');
        printWindow.document.write('</style></head><body>');
        printWindow.document.write('<h1>Receivable Outstanding</h1>');
        printWindow.document.write('<table><thead><tr>');
        printWindow.document.write('<th>SR. NO.</th>');
        printWindow.document.write('<th>TRADE NAME</th>');
        printWindow.document.write('<th>NAME</th>');
        printWindow.document.write('<th>ACCOUNT TYPE</th>');
        printWindow.document.write('<th>CITY</th>');
        printWindow.document.write('<th>MOBILE NUMBER</th>');
        printWindow.document.write('<th>BALANCE</th>');
        printWindow.document.write('</tr></thead><tbody>');

        filteredRows.forEach((r, idx) => {
            printWindow.document.write('<tr>');
            printWindow.document.write(`<td>${idx + 1}</td>`);
            printWindow.document.write(`<td>${r.tradeName || ''}</td>`);
            printWindow.document.write(`<td>${r.name || ''}</td>`);
            printWindow.document.write(`<td>${r.accountType || ''}</td>`);
            printWindow.document.write(`<td>${r.city || ''}</td>`);
            printWindow.document.write(`<td>${r.mobile || ''}</td>`);
            printWindow.document.write(`<td>₹${parseFloat(r.balance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>`);
            printWindow.document.write('</tr>');
        });

        const totalBalance = filteredRows.reduce((sum, r) => sum + (parseFloat(r.balance) || 0), 0);
        printWindow.document.write('<tr style="font-weight: bold; background-color: #eaeaea;">');
        printWindow.document.write('<td colspan="6" style="text-align: right;">TOTAL</td>');
        printWindow.document.write(`<td>₹${totalBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>`);
        printWindow.document.write('</tr>');

        printWindow.document.write('</tbody></table></body></html>');
        printWindow.document.close();
        printWindow.print();
    };

    const totalEntries = filteredRows.length;
    const totalPages = Math.ceil(totalEntries / pageSize);

    const showingFrom = totalEntries === 0 ? 0 : (page - 1) * pageSize + 1;
    const showingTo = Math.min(totalEntries, page * pageSize);

    const columns = [
        {
            key: 'sr_no',
            label: 'SR. NO.',
            render: (r, idx) => (page - 1) * pageSize + idx + 1
        },
        {
            key: 'tradeName',
            label: 'TRADE NAME',
            render: (r, idx) => (
                <div
                    className={`party-name-box ${idx === selectedIdx ? 'party-name-box--active' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedIdx(idx);
                        onNavigateToProfile && onNavigateToProfile(r);
                    }}
                >
                    {r.tradeName}
                </div>
            )
        },
        { key: 'name', label: 'NAME' },
        { key: 'accountType', label: 'ACCOUNT TYPE' },
        { key: 'city', label: 'CITY' },
        { key: 'mobile', label: 'MOBILE NUMBER' },
        {
            key: 'balance',
            label: 'BALANCE',
            style: { fontWeight: 'bold', color: '#1e293b' },
            render: (r) => `₹${parseFloat(r.balance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        }
    ];

    const footerRow = currentRows.length > 0 ? (
        <tr style={{ backgroundColor: '#eaeaea', fontWeight: 'bold' }}>
            <td colSpan={6} style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>TOTAL</td>
            <td style={{ fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>
                ₹{currentRows.reduce((sum, r) => sum + (parseFloat(r.balance) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </td>
        </tr>
    ) : null;

    if (loading) return <PageSkeleton />;

    return (
        <div className="list-page-container">
            <div className="list-page-header">
                <h1 className="list-page-title">Receivable Outstanding</h1>
                <div className="list-page-action-buttons">
                    <button className="list-page-btn list-page-btn-excel" onClick={handleExportExcel}>Excel</button>
                    <button className="list-page-btn list-page-btn-print" onClick={handlePrint}>Print</button>
                </div>
            </div>

      <DataTable
        columns={columns}
        data={currentRows}
        loading={loading}
        emptyMessage="No records found"
        page={page}
        pageSize={pageSize}
        totalEntries={totalEntries}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        footerRow={footerRow}
        onRowClick={(r, idx) => {
          setSelectedIdx(idx);
        }}
        onRowDoubleClick={(r) => onNavigateToProfile && onNavigateToProfile(r)}
        rowClassName={(r, idx) => idx === selectedIdx ? 'selected-row' : ''}
        searchBar={
          <>
            <div className="list-page-search-box">
              <SearchBar
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search customers..."
              />
            </div>
            <div className="list-filter-inline">
              <span className="list-filter-label">Filter:</span>
              <select
                className="list-filter-select"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
              >
                <option value="">---Select City---</option>
                {cityOptions.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
              <button className="list-filter-btn-submit" onClick={handleSubmit}>SUBMIT</button>
              <button className="list-filter-btn-reset" onClick={handleReset}>RESET</button>
            </div>
          </>
        }
      />
        </div>
    );
}

export default ReceivableList;
